import React from "react";
import { Box, Stack, Typography, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";

/**
 * MagazineNextIssueCard
 * 다음 호 안내 카드 - MagazineCollectionCard와 동일한 스타일
 * @param {object} props
 * @param {string} [props.nextIssue] - 다음 호 (기본값: "9월호")
 * @param {string} [props.nextTitle] - 다음 호 주제
 * @param {boolean} [props.isMobile]
 */
const MagazineNextIssueCard = ({ nextIssue = "9월호", nextTitle, isMobile }) => {
	const theme = useTheme();
    if (process.env.NODE_ENV === 'development') {
        // console.log("[MagazineNextIssueCard] props:", { nextIssue, nextTitle });
    }

	return (
		<Stack 
			spacing={isMobile ? 2 : 24} 
			sx={{ 
				width: "100%", 
				py: isMobile ? 3 : 5, 
				px: isMobile ? 2 : '32px',
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				textAlign: "left",
				minHeight: "200px",
				backgroundColor: 'transparent',
			}}
		>
			<Box sx={{ minWidth: 0 }}>
				<Box sx={{ textDecoration: "none" }}>
					<Typography
						variant={isMobile ? "body2" : "h5"}
						sx={{
							fontFamily:
								'"source-han-serif-korean", "Noto Serif KR", "Noto Serif CJK KR", "Source Han Serif KR", "Apple SD Gothic Neo", "Malgun Gothic", "맑은 고딕", serif',
							fontWeight: 900,
						color: theme.palette.text.primary,
							lineHeight: 1.4,
							height: isMobile ? "auto" : "72px",
							overflow: "hidden",
							wordBreak: "keep-all",
							display: "-webkit-box",
							WebkitLineClamp: isMobile ? 2 : 3,
							WebkitBoxOrient: "vertical",
							fontSize: isMobile ? "0.9rem" : undefined,
							mb: isMobile ? 2 : 3,
						}}
					>
						{nextTitle || `다음 ${nextIssue} 참여자를 찾습니다!`}
					</Typography>
					<Box sx={{
						display: "-webkit-box",
						WebkitLineClamp: isMobile ? 2 : 3,
						WebkitBoxOrient: "vertical",
						overflow: "hidden",
						height: isMobile ? "auto" : "88px",
						maxHeight: isMobile ? "4em" : "88px",
					}}>
						<Typography
							variant={isMobile ? "caption" : "body1"}
							sx={{
								fontFamily:
									'"source-han-serif-korean", "Noto Serif KR", "Noto Serif CJK KR", "Source Han Serif KR", "Apple SD Gothic Neo", "Malgun Gothic", "맑은 고딕", serif',
								fontWeight: 800,
								color: theme.palette.text.primary,
								wordBreak: "keep-all",
								lineHeight: 1.5,
								fontSize: isMobile ? "0.75rem" : undefined,
							}}
						>
							{nextIssue} 참여자를 찾습니다!
						</Typography>
					</Box>
				</Box>
			</Box>
			<Stack
				direction="row"
				spacing={1.5}
				alignItems="center"
				sx={{ minWidth: 0, width: "100%" }}
			>
				<Button
					variant="outlined"
					href="https://discord.com/invite/JWjRJ9RYk4"
					target="_blank"
					rel="noopener noreferrer"
					fullWidth
					startIcon={
						<svg
							width="20"
							height="20"
							viewBox="0 0 33 32"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<g clipPath="url(#clip0_discord)">
								<path
									d="M27.5928 6.20817C25.5533 5.27289 23.3662 4.58382 21.0794 4.18916C21.0378 4.18154 20.9962 4.20057 20.9747 4.23864C20.6935 4.73863 20.3819 5.3909 20.1637 5.90358C17.7042 5.53558 15.2573 5.53558 12.8481 5.90358C12.6299 5.37951 12.307 4.73863 12.0245 4.23864C12.003 4.20184 11.9614 4.18281 11.9198 4.18916C9.63431 4.58255 7.44721 5.27163 5.40641 6.20817C5.38874 6.21578 5.3736 6.22848 5.36355 6.24497C1.21508 12.439 0.078646 18.4809 0.636144 24.4478C0.638667 24.477 0.655064 24.5049 0.677768 24.5227C3.41481 26.5315 6.06609 27.7511 8.66815 28.5594C8.70979 28.5721 8.75392 28.5569 8.78042 28.5226C9.39594 27.6826 9.94461 26.7968 10.4151 25.8653C10.4428 25.8107 10.4163 25.746 10.3596 25.7244C9.48927 25.3945 8.66058 24.9922 7.86343 24.5354C7.80038 24.4986 7.79533 24.4084 7.85333 24.3653C8.02108 24.2397 8.18888 24.109 8.34906 23.977C8.37804 23.9529 8.41842 23.9478 8.45249 23.963C13.6894 26.3526 19.359 26.3526 24.5341 23.963C24.5682 23.9465 24.6086 23.9516 24.6388 23.9757C24.799 24.1077 24.9668 24.2397 25.1358 24.3653C25.1938 24.4084 25.19 24.4986 25.127 24.5354C24.3298 25.0011 23.5011 25.3945 22.6296 25.7232C22.5728 25.7447 22.5476 25.8107 22.5754 25.8653C23.0559 26.7955 23.6046 27.6812 24.2087 28.5213C24.234 28.5569 24.2794 28.5721 24.321 28.5594C26.9357 27.7511 29.5869 26.5315 32.324 24.5227C32.348 24.5049 32.3631 24.4783 32.3656 24.4491C33.0328 17.5506 31.2481 11.5584 27.6344 6.24623C27.6256 6.22848 27.6105 6.21578 27.5928 6.20817ZM11.1971 20.8146C9.62043 20.8146 8.32129 19.3679 8.32129 17.5913C8.32129 15.8146 9.59523 14.368 11.1971 14.368C12.8115 14.368 14.0981 15.8273 14.0729 17.5913C14.0729 19.3679 12.7989 20.8146 11.1971 20.8146ZM21.8299 20.8146C20.2533 20.8146 18.9541 19.3679 18.9541 17.5913C18.9541 15.8146 20.228 14.368 21.8299 14.368C23.4444 14.368 24.7309 15.8273 24.7057 17.5913C24.7057 19.3679 23.4444 20.8146 21.8299 20.8146Z"
									fill={theme.palette.primary.main}
								/>
							</g>
							<defs>
								<clipPath id="clip0_discord">
									<rect
										width="31.9995"
										height="32"
										fill="white"
										transform="translate(0.5)"
									/>
								</clipPath>
							</defs>
						</svg>
					}
					sx={{
						fontWeight: 700,
						fontSize: isMobile ? "14px" : "16px",
						height: isMobile ? 36 : 44,
							backgroundColor: 'transparent',
							color: theme.palette.text.primary + " !important",
							borderColor: theme.palette.text.primary,
							"&:hover": {
								backgroundColor: 'transparent',
								borderColor: theme.palette.text.primary,
								opacity: 0.85,
							},
					}}
				>
					디스코드로
				</Button>
			</Stack>
		</Stack>
	);
};

export default MagazineNextIssueCard;